import React from 'react';
import Icon from '../../../../components/AppIcon';
import styled from 'styled-components';

const APP_STORE_URL = 'https://apps.apple.com/app/id1436859571';
const GOOGLE_PLAY_URL =
  'https://play.google.com/store/apps/details?id=io.betareader.app';

export const resolveReaderAudioContentVersionId = ({
  currentBook,
  currentContentVersion,
}) => {
  const pinnedVersion = currentBook?.contentVersion;
  return (
    pinnedVersion?._id || pinnedVersion || currentContentVersion?._id || null
  );
};

const Callout = styled.aside`
  position: relative;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 0.85rem;
  margin: 0 0 2rem;
  padding: 1rem 1.1rem 1rem 1.25rem;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.navigation.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.navigation.raisedSurface};
  color: ${({ theme }) => theme.navigation.text};
  box-shadow: 0 7px 20px ${({ theme }) => theme.navigation.shadow};

  &::before {
    content: '';
    position: absolute;
    inset: 0 auto 0 0;
    width: 5px;
    background: ${({ theme }) => theme.navigation.accent};
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 0.65rem;
    padding: 0.9rem 1rem 0.95rem 1.15rem;
  }
`;

const AudioMark = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background: ${({ theme }) => theme.navigation.activeBackground};
  color: ${({ theme }) => theme.navigation.activeText};

  .icon {
    width: auto;
    margin: 0 !important;
  }

  @media (max-width: 480px) {
    width: 2.15rem;
    height: 2.15rem;
  }
`;

const Copy = styled.div`
  min-width: 0;
`;

const Title = styled.strong`
  display: block;
  margin-bottom: 0.25rem;
  color: ${({ theme }) => theme.navigation.heading};
  font-size: 0.95rem;
  line-height: 1.3;
`;

const Description = styled.p`
  margin: 0;
  font-size: 0.82rem;
  line-height: 1.45;
`;

const StoreLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
  margin-top: 0.65rem;

  a {
    padding: 0.34rem 0.62rem;
    border: 1px solid ${({ theme }) => theme.navigation.accent};
    border-radius: 999px;
    color: ${({ theme }) => theme.navigation.accent};
    font-size: 0.74rem;
    font-weight: 700;
    line-height: 1;
  }

  a:hover {
    background: ${({ theme }) => theme.navigation.activeBackground};
  }

  a:focus-visible {
    outline: 3px solid ${({ theme }) => theme.navigation.focus};
    outline-offset: 2px;
  }
`;

const ReaderAudioAccess = ({
  audioAvailable = false,
  isAuthor = false,
  isWrapped = false,
  children,
}) => {
  // WebView messaging controls presentation. Firebase App Check remains an
  // authorization boundary inside BRNative when it requests the protected
  // stream; a transient proof failure must not make the app advertise itself.
  if (isAuthor || (audioAvailable && isWrapped)) {
    return children;
  }

  if (!audioAvailable) return null;

  return (
    <Callout aria-label="Audiobook access">
      <AudioMark aria-hidden="true">
        <Icon name="headphones" />
      </AudioMark>
      <Copy>
        <Title>Audio available in the BetaReader app</Title>
        <Description>
          Keep reading here, or use the app for the complete listening
          experience.
        </Description>
        <StoreLinks>
          <a href={APP_STORE_URL}>App Store</a>
          <a href={GOOGLE_PLAY_URL}>Google Play</a>
        </StoreLinks>
      </Copy>
    </Callout>
  );
};

export default ReaderAudioAccess;
